import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Download, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Starting point for a new survey. Field types: single, multi, scale, text, email, checkbox.
const TEMPLATE_STEPS = [
  {
    title: "What best describes you?",
    hint: "Pick the one that fits best.",
    fields: [{
      id: "role", type: "single", required: true,
      options: ["Backend engineer", "Frontend / full-stack", "Engineering manager", "Founder / product", "Student", "Other"]
        .map((l) => ({ value: l.toLowerCase().replace(/[^a-z0-9]+/g, "-"), label: l })),
    }],
  },
  {
    title: "Which project should I start next?",
    hint: "A short note on the ideas you're weighing.",
    fields: [{
      id: "idea", type: "single", required: true,
      options: [
        { value: "a", label: "Project idea A", desc: "One line on what it does and who it is for." },
        { value: "b", label: "Project idea B", desc: "One line on what it does and who it is for." },
        { value: "c", label: "Project idea C", desc: "One line on what it does and who it is for." },
      ],
    }],
  },
  {
    title: "How much would you use it?",
    hint: "1 is rarely, 5 is every day.",
    fields: [{ id: "interest", type: "scale", required: true, min_label: "Rarely", max_label: "Every day" }],
  },
  {
    title: "Which features matter most?",
    hint: "Pick as many as you like.",
    fields: [{
      id: "features", type: "multi",
      options: ["Open source", "Self-hostable", "Great docs", "Low latency", "Cheap to run", "Mobile app", "API first", "Live metrics"]
        .map((l) => ({ value: l.toLowerCase().replace(/[^a-z0-9]+/g, "-"), label: l })),
    }],
  },
  {
    title: "Anything else?",
    hint: "All optional.",
    fields: [
      { id: "note", type: "text", label: "Anything else I should know?" },
      { id: "email", type: "email", label: "Email, if you'd like early access" },
      { id: "followup", type: "checkbox", label: "Happy to do a 20-minute follow-up call" },
    ],
  },
];

const BLANK = {
  slug: "", title: "Help me choose my next project", intro: "I'm deciding what to build next and how you'd use it.",
  thanks: "Your answers have been saved. I'll share what I learn on the blog.", active: true,
  steps: TEMPLATE_STEPS,
};

export default function AdminSurveys() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [stepsText, setStepsText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [responses, setResponses] = useState([]);

  const load = () => api.get("/surveys").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setDraft(BLANK); setStepsText(JSON.stringify(BLANK.steps, null, 2)); setEditingId(null); setOpen(true);
  };
  const openEdit = (s) => {
    setDraft(s); setStepsText(JSON.stringify(s.steps, null, 2)); setEditingId(s.id); setOpen(true);
  };

  const save = async () => {
    let steps;
    try { steps = JSON.parse(stepsText); } catch (e) { toast.error(`Questions JSON: ${e.message}`); return; }
    const body = { slug: draft.slug, title: draft.title, intro: draft.intro, thanks: draft.thanks, active: draft.active, steps };
    try {
      if (editingId) await api.put(`/surveys/${editingId}`, body);
      else await api.post("/surveys", body);
      setOpen(false); toast.success("Saved."); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and all of its answers?`)) return;
    try { await api.delete(`/surveys/${s.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (s) => {
    try { await api.put(`/surveys/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const view = async (s) => {
    setViewing(s);
    setResponses([]);
    try { const { data } = await api.get(`/surveys/${s.id}/responses`); setResponses(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fields = viewing ? viewing.steps.flatMap((st) => st.fields.map((f) => ({ ...f, title: f.label || st.title }))) : [];

  const downloadCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["submitted_at", ...fields.map((f) => f.id)].map(esc).join(",")];
    responses.forEach((r) => rows.push([r.created_at, ...fields.map((f) => show(r.answers[f.id], f))].map(esc).join(",")));
    const url = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${viewing.slug}-responses.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Survey</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Surveys</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            The newest active survey shows at /survey. Any active survey also has its own link at /survey/&lt;slug&gt;.
          </p>
        </div>
        <Button onClick={openNew} data-testid="survey-new"><Plus className="h-4 w-4" /> New survey</Button>
      </header>

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left label-xs">
              <th className="p-3">Title</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Steps</th>
              <th className="p-3 w-24">Active</th>
              <th className="p-3 w-64"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-muted-foreground">No surveys yet. Start one from the template.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="p-3 font-medium cursor-pointer" onClick={() => openEdit(s)}>{s.title}</td>
                <td className="p-3 text-muted-foreground font-mono text-xs">
                  <a href={`/survey/${s.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
                    {s.slug} <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
                <td className="p-3 text-muted-foreground font-mono text-xs">{s.steps.length}</td>
                <td className="p-3"><input type="checkbox" checked={s.active} onChange={() => toggle(s)} aria-label="Active" /></td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => view(s)}>Answers</Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && (
        <section className="mt-10">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <p className="label-xs">Answers</p>
              <h2 className="mt-2 font-heading font-bold text-2xl tracking-tight">{viewing.title} · {responses.length}</h2>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!responses.length}><Download className="h-4 w-4" /> CSV</Button>
              <Button variant="ghost" size="sm" onClick={() => setViewing(null)}>Close</Button>
            </div>
          </div>
          <div className="mt-4 border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left label-xs">
                  <th className="p-3 whitespace-nowrap">When</th>
                  {fields.map((f) => <th key={f.id} className="p-3 min-w-[10rem]">{f.title}</th>)}
                </tr>
              </thead>
              <tbody>
                {responses.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    {fields.map((f) => <td key={f.id} className="p-3">{show(r.answers[f.id], f)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit survey" : "New survey"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <FF label="Slug (used in the link)" v={draft.slug} on={(v) => setDraft({ ...draft, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
              <label className="flex items-end gap-2 text-sm pb-2">
                <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active
              </label>
            </div>
            <FF label="Title" v={draft.title} on={(v) => setDraft({ ...draft, title: v })} />
            <FT label="Intro" v={draft.intro} on={(v) => setDraft({ ...draft, intro: v })} rows={2} />
            <FT label="Thank-you message" v={draft.thanks} on={(v) => setDraft({ ...draft, thanks: v })} rows={2} />
            <div>
              <Label className="label-xs">Steps (JSON)</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Each step has a title, an optional hint, and fields. Field types: single, multi, scale (1–5), text, email, checkbox.
                Choice fields need options with a value and label (desc is optional and turns them into big cards).
                Keep field ids stable once answers come in.
              </p>
              <Textarea rows={18} value={stepsText} onChange={(e) => setStepsText(e.target.value)} className="mt-2 font-mono text-xs" spellCheck={false} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="survey-save"><Save className="h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function show(v, field) {
  if (v === undefined || v === null) return "";
  const label = (x) => field.options?.find((o) => o.value === x)?.label ?? x;
  if (Array.isArray(v)) return v.map(label).join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(label(v));
}

function FF({ label, v, on, type = "text" }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" />
    </div>
  );
}
function FT({ label, v, on, rows = 3 }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" />
    </div>
  );
}
